import { cn } from "@/lib/utils";

export type CancellationStepState = "done" | "current" | "pending" | "final";

export interface CancellationStep {
  label: string;
  caption: string;
  state: CancellationStepState;
}

const STATE_LABELS: Record<CancellationStepState, string> = {
  done: "concluída",
  current: "em andamento",
  pending: "pendente",
  final: "concluída",
};

const StepNode = ({ state }: { state: CancellationStepState }) => (
  <span
    aria-hidden="true"
    className={cn(
      "relative z-10 flex h-8 w-8 items-center justify-center rounded-full",
      state === "done" && "bg-primary",
      state === "current" && "border-2 border-amber-500 bg-background",
      state === "pending" && "bg-muted",
      state === "final" && "bg-foreground"
    )}
  >
    {state === "final" ? (
      <span className="h-0.5 w-3 bg-background" />
    ) : (
      <span
        className={cn(
          "h-2.5 w-2.5 rounded-full",
          state === "done" && "bg-primary-foreground",
          state === "current" && "bg-amber-500",
          state === "pending" && "bg-muted-foreground/40"
        )}
      />
    )}
  </span>
);

interface CancellationStepperProps {
  steps: CancellationStep[];
  className?: string;
  /** Também usado no acompanhamento das solicitações por item. */
  ariaLabel?: string;
}

export const CancellationStepper = ({ steps, className, ariaLabel = "Etapas do cancelamento" }: CancellationStepperProps) => (
  <ol aria-label={ariaLabel} className={cn("flex", className)}>
    {steps.map((step, index) => {
      const isPending = step.state === "pending";
      return (
        <li
          key={step.label}
          aria-current={step.state === "current" ? "step" : undefined}
          className="relative flex flex-1 flex-col items-center gap-2 px-1 text-center"
        >
          {index > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute right-1/2 top-4 h-0.5 w-full -translate-y-1/2",
                isPending ? "bg-muted" : "bg-primary"
              )}
            />
          )}
          <StepNode state={step.state} />
          <span
            className={cn(
              "text-[13px] font-semibold leading-tight",
              isPending ? "text-muted-foreground/70" : "text-foreground"
            )}
          >
            {step.label}
            <span className="sr-only"> — etapa {STATE_LABELS[step.state]}</span>
          </span>
          <span className={cn("text-xs leading-tight", isPending ? "text-muted-foreground/70" : "text-muted-foreground")}>
            {step.caption}
          </span>
        </li>
      );
    })}
  </ol>
);
